import EmailIcon from '@mui/icons-material/Email'
import MessageIcon from '@mui/icons-material/Message'
import PhoneIcon from '@mui/icons-material/Phone'
import { Box, Tooltip, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'

import { AlertDialog, MButton } from 'components'
import { ADMIN_CONTACT_EMAIL, ROUTES_PATH } from 'config'
import { color } from 'config/color'
import { useAuth } from 'context'
import { isEmpty } from 'lodash'
import { useState } from 'react'
import { isAuthorized, useMobileBreakpoints } from 'utils'

interface ICampaignListCard {
  id: string
  name: string
  htmlTemplate: string
  plainTextTemplate: string
  smsTemplate: string
  ivrTemplate: string
  initCampaigns: () => Promise<void>
}

export const CampaignListCard = (props: ICampaignListCard): JSX.Element => {
  const navigate = useNavigate()
  const isMobile = useMobileBreakpoints()

  const { deleteCampaign, handleLogOut } = useAuth()

  const [dialogData, setDialogData] = useState<any>({
    type: '',
    isOpen: false,
    title: '',
    message: ''
  })

  const [confirmDialog, setConfirmDialog] = useState<any>({
    type: 'warning',
    isOpen: false,
    title: 'Delete Campaign',
    message:
      'You’re about to delete a campaign. Please be aware that this action can’t be undone.'
  })

  const handleDelete = async (showConfirmDialog: boolean): Promise<void> => {
    try {
      if (showConfirmDialog) {
        setConfirmDialog({
          ...confirmDialog,
          type: 'error',
          isOpen: true
        })
        return
      }

      await deleteCampaign(props.id)
      setDialogData({
        type: 'success',
        isOpen: true,
        title: 'Campaign Deleted',
        message: 'You have successfully deleted a campaign.'
      })
    } catch (error: any) {
      if (!isAuthorized(error)) await handleLogOut()

      setDialogData({
        type: 'error',
        isOpen: true,
        title: 'An error occured',
        message: `We are really sorry but there was a problem and we couldn’t delete your campaign. Please try again, if the error persist,  <a href="mailto:${ADMIN_CONTACT_EMAIL}" >Contact Us</a>.`
      })
    }
  }

  return (
    <Box
      p={1.5}
      display="flex"
      flexDirection="column"
      bgcolor={color.grey[50]}
      border={`1px solid ${color.secondary.dark[100]}`}
      height={isMobile ? '18vh' : '16vh'}
      justifyContent="space-between"
    >
      <Box display="flex" gap={1} justifyContent="space-between">
        <Box width="45%">
          {props.name.length >= 40
            ? (
              <Tooltip
                title={props.name}
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      color: color.grey[900],
                      backgroundColor: color.white,
                      border: `1px solid ${color.secondary.dark[300]}`
                    }
                  },
                  arrow: {
                    sx: {
                      '&:before': {
                        border: `1px solid ${color.secondary.dark[300]}`,
                        color: color.white
                      }
                    }
                  }
                }}
              >
                <Typography fontSize={18} color={color.grey[900]}>
                  {`${props.name.slice(0, 26)} ...`}
                </Typography>
              </Tooltip>
            )
            : (
              <Typography fontSize={18} color={color.grey[900]}>
                {props.name}
              </Typography>
            )}
        </Box>

        <Box display="flex" gap={0.5} alignItems="flex-start">
          {(!isEmpty(props.htmlTemplate) ||
            !isEmpty(props.plainTextTemplate)) && (
              <Box
                display="flex"
                bgcolor={color.white}
                borderRadius="20%"
                p={0.25}
                border={`1px solid ${color.secondary.dark[200]}`}
              >
                <EmailIcon sx={{ color: color.secondary.dark[500] }} />
              </Box>
            )}

          {!isEmpty(props.ivrTemplate) && (
            <Box
              display="flex"
              bgcolor={color.white}
              borderRadius="20%"
              p={0.25}
              border={`1px solid ${color.secondary.dark[200]}`}
            >
              <PhoneIcon sx={{ color: color.secondary.dark[500] }} />
            </Box>
          )}

          {!isEmpty(props.smsTemplate) && (
            <Box
              display="flex"
              bgcolor={color.white}
              borderRadius="20%"
              p={0.25}
              border={`1px solid ${color.secondary.dark[200]}`}
            >
              <MessageIcon sx={{ color: color.secondary.dark[500] }} />
            </Box>
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap={2}>
          <MButton
            component="label"
            type="button"
            size="medium"
            rounded="true"
            sx={{ fontSize: 16, padding: 0 }}
            texttransform="none"
            onClick={() => {
              navigate(`${ROUTES_PATH.CAMPAIGNS}?id=${props.id}`)
            }}
          >
            <Typography color={color.primary[600]}>Send/Edit</Typography>
          </MButton>

          <MButton
            component="label"
            type="button"
            size="medium"
            rounded="true"
            sx={{ fontSize: 16, padding: 0 }}
            texttransform="none"
            onClick={() => {
              navigate(`${ROUTES_PATH.CAMPAIGN_LOGS}?id=${props.id}`)
            }}
          >
            <Typography color={color.primary[600]}>View Logs</Typography>
          </MButton>
        </Box>

        <MButton
          component="label"
          type="button"
          size="medium"
          rounded="true"
          sx={{ fontSize: 16, padding: 0 }}
          texttransform="none"
          onClick={() => {
            void handleDelete(true)
          }}
        >
          <Typography color={color.grey[800]}>Delete</Typography>
        </MButton>

        <AlertDialog
          open={dialogData.isOpen}
          type={dialogData.type}
          title={dialogData.title}
          message={dialogData.message}
          handleClick={() => {
            setDialogData({ ...dialogData, isOpen: false })
            void props.initCampaigns()
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            gap={1}
          >
            <MButton
              variant="outlined"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={() => {
                setDialogData({ ...dialogData, isOpen: false })
              }}
            >
              Go Back to Campaigns
            </MButton>
          </Box>
        </AlertDialog>

        <AlertDialog
          open={confirmDialog.isOpen}
          type={confirmDialog.type}
          title={confirmDialog.title}
          message={confirmDialog.message}
          handleClick={() => {
            setConfirmDialog({ ...confirmDialog, isOpen: false })
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            gap={1}
          >
            <MButton
              variant="contained"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={() => {
                setConfirmDialog({ ...confirmDialog, isOpen: false })
                void handleDelete(false)
              }}
            >
              Delete Campaign
            </MButton>
            <MButton
              variant="outlined"
              type="button"
              rounded="true"
              texttransform="none"
              onClick={() => {
                setConfirmDialog({ ...confirmDialog, isOpen: false })
              }}
            >
              Go Back to Campaigns
            </MButton>
          </Box>
        </AlertDialog>
      </Box>
    </Box>
  )
}
